import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const ROLE_BADGE = {
  admin: { bg: "#7b1f2b", fg: "white" },
  organisateur: { bg: "#c98a3a", fg: "white" },
  benevole: { bg: "#4f6b3a", fg: "white" },
  artiste: { bg: "#7a6f9b", fg: "white" },
  participant: { bg: "#7a6f60", fg: "white" },
  invitation: { bg: "#9a8d7a", fg: "white" },
};

export default function RHTab({ locale = "fr" }) {
  const isFr = locale === "fr";
  const [data, setData] = useState({ admins: [], members: [], invitations: [],
                                       role_counts: {}, function_counts: {},
                                       function_labels: {}, role_labels: {} });
  const [roleFilter, setRoleFilter] = useState("");
  const [functionFilter, setFunctionFilter] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      const r = await api.get("/admin/hr/overview");
      setData(r.data);
    })();
  }, []);

  // Combined rows
  const rows = useMemo(() => {
    const out = [];
    for (const a of data.admins) {
      out.push({ ...a, _kind: "admin", _role: "admin", _function: "", _status: "active" });
    }
    for (const m of data.members) {
      out.push({ ...m, _kind: "member", _role: m.role, _function: m.function || "",
                  _status: m.active === false ? "inactive" : "active" });
    }
    for (const i of data.invitations) {
      out.push({ ...i, _kind: "invitation", _role: i.role || "organisateur",
                  _function: i.function || "", _status: "pending" });
    }
    return out;
  }, [data]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (roleFilter && r._role !== roleFilter) return false;
      if (functionFilter && r._function !== functionFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!(r.name || "").toLowerCase().includes(q) &&
            !(r.email || "").toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [rows, roleFilter, functionFilter, search]);

  return (
    <div className="space-y-6" data-testid="rh-tab">
      <h2 className="font-display text-2xl">{isFr ? "Ressources humaines" : "Human resources"}</h2>

      <div className="grid sm:grid-cols-5 gap-3">
        {Object.entries(data.role_counts).map(([role, count]) => (
          <button key={role} onClick={() => setRoleFilter(roleFilter === role ? "" : role)}
                  className="admin-card text-center"
                  data-testid={`rh-kpi-${role}`}
                  style={{
                    borderColor: roleFilter === role ? "var(--cinnabar)" : "var(--border)",
                    borderWidth: 2,
                    cursor: "pointer",
                  }}>
            <div className="font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>
              {data.role_labels[role] || role}
            </div>
            <div className="font-display text-3xl" style={{ color: "var(--gold)" }}>{count}</div>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input className="input-classic flex-1 min-w-[200px]"
               placeholder={isFr ? "Rechercher nom ou email…" : "Search name or email…"}
               value={search} onChange={(e) => setSearch(e.target.value)}
               data-testid="rh-search" />
        <select className="input-classic" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="rh-filter-role">
          <option value="">{isFr ? "Tous rôles" : "All roles"}</option>
          {Object.keys(data.role_counts).map((r) => (
            <option key={r} value={r}>{data.role_labels[r] || r}</option>
          ))}
        </select>
        <select className="input-classic" value={functionFilter} onChange={(e) => setFunctionFilter(e.target.value)} data-testid="rh-filter-function">
          <option value="">{isFr ? "Toutes fonctions" : "All functions"}</option>
          {Object.entries(data.function_counts).sort().map(([fn, count]) => (
            <option key={fn} value={fn}>{(data.function_labels[fn] || fn)} ({count})</option>
          ))}
        </select>
        <span className="text-sm" style={{ color: "var(--muted)" }}>
          {filtered.length} / {rows.length}
        </span>
      </div>

      <div className="admin-card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border)" }}>
              <th className="text-left p-2 font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{isFr ? "Nom" : "Name"}</th>
              <th className="text-left p-2 font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>Email</th>
              <th className="text-left p-2 font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{isFr ? "Rôle" : "Role"}</th>
              <th className="text-left p-2 font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{isFr ? "Fonction" : "Function"}</th>
              <th className="text-left p-2 font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>{isFr ? "Statut" : "Status"}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => {
              const badgeKey = r._kind === "invitation" ? "invitation" : r._role;
              const badge = ROLE_BADGE[badgeKey] || ROLE_BADGE.participant;
              return (
                <tr key={`${r._kind}-${r.id || i}`} style={{ borderBottom: "1px solid var(--border)" }} data-testid={`rh-row-${i}`}>
                  <td className="p-2">{r.name || "—"}</td>
                  <td className="p-2 text-xs" style={{ color: "var(--ink-2)" }}>{r.email}</td>
                  <td className="p-2">
                    <span className="px-2 py-0.5 text-[0.65rem] rounded-sm font-caps"
                          style={{ backgroundColor: badge.bg, color: badge.fg, letterSpacing: "0.15em" }}>
                      {data.role_labels[r._role] || r._role}
                    </span>
                  </td>
                  <td className="p-2 text-xs">{data.function_labels[r._function] || r._function || "—"}</td>
                  <td className="p-2 text-xs" style={{ color: r._status === "active" ? "var(--moss)" : (r._status === "pending" ? "var(--cinnabar)" : "var(--muted)") }}>
                    {r._status === "pending" ? (isFr ? "Invitation en attente" : "Pending invitation")
                      : (r._status === "active" ? (isFr ? "Actif" : "Active") : (isFr ? "Inactif" : "Inactive"))}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan="5" className="p-6 text-center" style={{ color: "var(--muted)" }}>
                {isFr ? "Aucun résultat" : "No results"}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
